import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import {AlertTriangle, Check, ClipboardPaste, Search} from 'lucide-react';
import {SubscriptionProduct} from '../../types/graphql';
import {formatMoney, productKindLabel} from '../../utils/subscriptions';
import {OfferProductGroup} from '../../utils/subscriptionOffers';
import {Pill} from '../SubscriptionBadges';

interface ProductPickerProps {
  /** The element the popover hangs off: the line's product button, or "Add product". */
  anchor: HTMLElement;
  groups: OfferProductGroup[];
  /** The product on the line being changed, marked as current. */
  selectedId?: string;
  /** Products already on another line: shown, but not pickable twice. */
  takenIds: Set<string>;
  onSelect: (product: SubscriptionProduct) => void;
  /** Switch to typing an id, for a managed product or anything else the catalog does not list. */
  onPasteId: () => void;
  /** Managed products in the catalog; the switch is only offered when there are some. */
  managedCount: number;
  /** Whether `groups` includes the managed products. */
  showManaged: boolean;
  onShowManagedChange: (show: boolean) => void;
  onClose: () => void;
}

/** What a product is, in the picker's second line. Billing-only lines say so, as the table did. */
export function productKind(product: SubscriptionProduct): string {
  return productKindLabel(product) ?? 'add-on / no CWAY function';
}

const PANEL_WIDTH = 640;
/** Below this the section rail is dropped and the list takes the whole panel. */
const RAIL_MIN_WIDTH = 560;
const PANEL_HEIGHT = 440;
const GAP = 4;
const MARGIN = 8;

interface Position {
  left: number;
  width: number;
  height: number;
  top?: number;
  bottom?: number;
}

/**
 * Fixed to the viewport and portalled out of the modal: the line table scrolls sideways and the
 * modal scrolls vertically, and either would clip an absolutely positioned panel. Opens below the
 * anchor unless the space above is the roomier one.
 */
function usePosition(anchor: HTMLElement): Position {
  const [position, setPosition] = useState<Position>({left: 0, top: 0, width: PANEL_WIDTH, height: PANEL_HEIGHT});
  useLayoutEffect(() => {
    const update = () => {
      const rect = anchor.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width, PANEL_WIDTH), window.innerWidth - 2 * MARGIN);
      const left = Math.min(Math.max(MARGIN, rect.left), window.innerWidth - width - MARGIN);
      const below = window.innerHeight - rect.bottom - GAP - MARGIN;
      const above = rect.top - GAP - MARGIN;
      if (below >= 280 || below >= above) {
        setPosition({left, width, top: rect.bottom + GAP, height: Math.min(PANEL_HEIGHT, below)});
      } else {
        setPosition({left, width, bottom: window.innerHeight - rect.top + GAP, height: Math.min(PANEL_HEIGHT, above)});
      }
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchor]);
  return position;
}

/** Every search word has to appear in the name, id, kind or section — "pro sharing" finds Sharing Pro. */
function matches(product: SubscriptionProduct, group: OfferProductGroup, words: string[]): boolean {
  const haystack = `${product.name} ${product.id} ${productKind(product)} ${group.label}`.toLowerCase();
  return words.every((word) => haystack.includes(word));
}

export default function ProductPicker({
  anchor,
  groups,
  selectedId,
  takenIds,
  onSelect,
  onPasteId,
  managedCount,
  showManaged,
  onShowManagedChange,
  onClose,
}: ProductPickerProps) {
  const [search, setSearch] = useState('');
  /** The section the rail narrows to, or null for the whole catalog. */
  const [section, setSection] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const position = usePosition(anchor);
  const showRail = position.width >= RAIL_MIN_WIDTH && groups.length > 1;

  const matchingGroups = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    return groups.map((group) => ({...group, products: group.products.filter((product) => matches(product, group, words))}));
  }, [groups, search]);
  const matchCount = matchingGroups.reduce((sum, group) => sum + group.products.length, 0);
  const visibleGroups = matchingGroups.filter((group) => group.products.length > 0 && (!showRail || section === null || group.key === section));

  /** Keyboard order: the pickable products as they are laid out, top to bottom. */
  const pickable = useMemo(
    () => visibleGroups.flatMap((group) => group.products).filter((product) => !takenIds.has(product.id)),
    [visibleGroups, takenIds],
  );
  const [activeId, setActiveId] = useState<string | null>(selectedId ?? null);
  const active = pickable.find((product) => product.id === activeId) ?? pickable[0] ?? null;

  useEffect(() => {
    panelRef.current?.querySelector(`[data-product-id="${CSS.escape(active?.id ?? '')}"]`)?.scrollIntoView?.({block: 'nearest'});
  }, [active?.id]);

  useEffect(() => {
    const closeOnOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!panelRef.current?.contains(target) && !anchor.contains(target)) onClose();
    };
    document.addEventListener('mousedown', closeOnOutside);
    return () => document.removeEventListener('mousedown', closeOnOutside);
  }, [anchor, onClose]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      anchor.focus();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (pickable.length === 0) return;
      const index = active ? pickable.indexOf(active) : -1;
      const next = event.key === 'ArrowDown' ? Math.min(index + 1, pickable.length - 1) : Math.max(index - 1, 0);
      setActiveId(pickable[next].id);
    } else if (event.key === 'Enter') {
      // Never let Enter reach the offer form: it would submit the offer.
      event.preventDefault();
      if (active) onSelect(active);
    }
  };

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Choose a product"
      onKeyDown={handleKeyDown}
      // A fixed height rather than a maximum, so the panel holds still while a search narrows the list.
      style={{left: position.left, top: position.top, bottom: position.bottom, width: position.width, height: position.height}}
      // Above the offer modal's z-[60].
      className="fixed z-[70] flex flex-col overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-2xl"
    >
      <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 px-3">
        <Search size={16} className="shrink-0 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search products…"
          aria-label="Search products"
          ref={searchRef}
          autoFocus
          className="w-full bg-transparent py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none"
        />
      </div>

      <div className="flex min-h-0 flex-1">
        {showRail && (
          <nav
            aria-label="Sections"
            className="w-44 shrink-0 overflow-y-auto border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-1"
          >
            {[
              {key: null, label: 'All products', count: matchCount},
              ...matchingGroups.map((group) => ({key: group.key, label: group.label, count: group.products.length})),
            ].map((entry) => (
              <button
                key={entry.key ?? 'all'}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setSection(entry.key);
                  searchRef.current?.focus();
                }}
                aria-pressed={section === entry.key}
                className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm ${
                  section === entry.key
                    ? 'bg-blue-100 dark:bg-blue-900/40 font-medium text-blue-800 dark:text-blue-200'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60'
                } ${entry.count === 0 ? 'opacity-40' : ''}`}
              >
                <span className="truncate">{entry.label}</span>
                <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">{entry.count}</span>
              </button>
            ))}
          </nav>
        )}
        <div role="listbox" aria-label="Products" className="flex-1 overflow-y-auto py-1">
          {visibleGroups.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              {groups.length === 0
                ? 'The catalog is loading…'
                : matchCount > 0
                  ? 'Nothing in this section matches — try All products'
                  : `No product matches “${search}”`}
            </p>
          )}
          {visibleGroups.map((group) => (
            <div key={group.key} role="group" aria-label={group.label}>
              <div className="sticky top-0 z-10 flex items-baseline justify-between bg-white/95 dark:bg-gray-800/95 px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {group.label}
                <span className="font-normal normal-case tracking-normal text-gray-400 dark:text-gray-500">{group.products.length}</span>
              </div>
              {group.products.map((product) => {
                const taken = takenIds.has(product.id);
                const isActive = active?.id === product.id;
                const isSelected = selectedId === product.id;
                return (
                  <div
                    key={product.id}
                    role="option"
                    aria-label={`${product.name} — ${productKind(product)}${product.isManaged ? ' (managed)' : ''}`}
                    aria-selected={isSelected}
                    aria-disabled={taken}
                    data-product-id={product.id}
                    onMouseMove={() => !taken && setActiveId(product.id)}
                    // mousedown would move focus off the search field before the click lands.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => !taken && onSelect(product)}
                    className={`mx-1 flex items-center gap-3 rounded-md px-2 py-1.5 ${
                      taken ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                    } ${isActive ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                  >
                    <span className="flex w-4 shrink-0 justify-center text-blue-600 dark:text-blue-400">{isSelected && <Check size={14} />}</span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{product.name}</span>
                        {product.isManaged && (
                          <Pill tone="amber" title="The customer cannot add seats or cancel the subscription themselves">
                            managed
                          </Pill>
                        )}
                      </span>
                      <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                        {productKind(product)} · <span className="font-mono">{product.id}</span>
                      </span>
                    </span>
                    <span className="shrink-0 text-right text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {taken ? 'in offer' : `${formatMoney(product.price.amount, product.price.currency)} / mo`}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 px-3 py-2">
        {managedCount > 0 ? (
          // Keep focus in the search field, so the keyboard still drives the list.
          <label
            onMouseDown={(event) => event.preventDefault()}
            className="flex min-w-0 cursor-pointer items-center gap-2 text-xs text-gray-600 dark:text-gray-300"
          >
            <button
              type="button"
              role="switch"
              aria-checked={showManaged}
              aria-label="Show managed products"
              onClick={() => onShowManagedChange(!showManaged)}
              className={`relative inline-flex h-4 w-7 shrink-0 rounded-full transition-colors ${
                showManaged ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                aria-hidden="true"
                className={`absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform ${showManaged ? 'translate-x-3' : ''}`}
              />
            </button>
            {showManaged ? (
              <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
                <AlertTriangle size={12} className="shrink-0" />
                Managed shown — they lock the subscription to staff changes
              </span>
            ) : (
              <span>Show managed ({managedCount})</span>
            )}
          </label>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={onPasteId}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          <ClipboardPaste size={12} /> Paste a product id
        </button>
      </div>
    </div>,
    document.body,
  );
}
